import { capacitorErrorMessage } from "../capacitorErrors";
import { assertHealthSyncAvailable } from "./syncCoordination";
import { importSleepRecords, type SleepImportRow } from './sleepImport';
import { mutateHealthHistory, type HealthHistoryTable, type HealthHistoryMutationClient } from './historyMutation';
import type { HealthPlugin, HealthSample, Workout } from "@capgo/capacitor-health";
import {
  aggregateSamplesByDay, buildMetricRows, buildSleepRows, buildWorkoutRows,
  dayStringInZone, startOfDayInZoneUtc, type HealthProvider,
} from "./healthMap";
import { HEALTH_READ_TYPES, type HealthReadType } from "./permissionState";
import { bodyHealthTimestampCompare, validBodyHealthTimestamp } from '../../../supabase/functions/_shared/bodyHealthActionDomain';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- Heterogeneous JSONB/SQL rows are validated at the SDK boundary.
type Json = Record<string, any>;
type SyncType = "sleep" | "workouts" | "steps" | "heart_rate" | "hrv";
export interface HealthHistoryCoverage {
  attempted: boolean; read: number; written: number; protected: number;
  // Optional for checkpoints written by older clients. This is read evidence,
  // not an acknowledgement recovered from a previous run.
  unchanged?: number;
  complete: boolean; reason?: string;
}
export interface HealthHistoryState extends Json {
  version: 1; generation: string; from: string; to: string; next_before: string;
  completed_windows: number; status: "in_progress" | "complete" | "partial" | "blocked";
  window: { from: string; to: string; complete: boolean };
  coverage_by_type: Record<SyncType, HealthHistoryCoverage>;
  reason?: string; run_id?: string | null; lease_until?: string | null;
}
export interface HealthHistoryResult {
  synced: number; total_fetched: number; errors: string[]; status: "ok" | "partial";
  history: HealthHistoryState;
}
export interface HealthHistoryOptions {
  Health: Pick<HealthPlugin, "checkAuthorization" | "readSamples" | "queryWorkouts" | "queryAggregated">;
  // The owner-authenticated SDK client is intentional: no service-role bypass.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- This SDK bridge includes columns not yet in generated Database types.
  sb: { from: (table: string) => any; rpc?: HealthHistoryMutationClient["rpc"]; auth: { getUser: () => Promise<any> } };
  userId: string; provider: HealthProvider; connection: { id: string; provider_metadata?: unknown };
  history_from?: string; mode: "history" | "history_continue";
  current?: () => boolean; now?: Date; timezone?: string;
}
const DAY = 86_400_000;
const PAGE = 500;
const TYPES: SyncType[] = ["sleep", "workouts", "steps", "heart_rate", "hrv"];
const READ_TYPE: Record<SyncType, HealthReadType> = {
  sleep: "sleep", workouts: "workouts", steps: "steps", heart_rate: "heartRate", hrv: "heartRateVariability",
};
const object = (value: unknown): Json => value && typeof value === "object" && !Array.isArray(value) ? value as Json : {};
const message = capacitorErrorMessage;
function jsonValue(value: unknown): boolean {
  if (value === null || typeof value === "string" || typeof value === "boolean") return true;
  if (typeof value === "number") return Number.isFinite(value);
  if (Array.isArray(value)) return value.every(jsonValue);
  return value !== null && typeof value === "object"
    && (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null)
    && Object.values(value).every(jsonValue);
}
function canonical(value: unknown): string {
  if (Array.isArray(value)) return "[" + value.map(canonical).join(",") + "]";
  if (value && typeof value === "object") return "{" + Object.keys(value).sort().map(key => JSON.stringify(key) + ":" + canonical(value[key])).join(",") + "}";
  return JSON.stringify(value);
}
function matchesPayload(saved: Json, payload: Json, precise = false): boolean {
  if (!jsonValue(saved) || !jsonValue(payload)) return false;
  return Object.keys(payload).every(key => {
    if (["started_at", "completed_at", "recorded_at", "ended_at"].includes(key) && payload[key] != null) {
      return precise ? validBodyHealthTimestamp(saved[key]) && bodyHealthTimestampCompare(saved[key], payload[key]) === 0
        : timestamp(saved[key]) === timestamp(payload[key]);
    }
    if (["bedtime", "wakeup"].includes(key)) return String(saved[key]).slice(0, 5) === String(payload[key]).slice(0, 5);
    return canonical(saved[key]) === canonical(payload[key]);
  });
}
const successful = (reply: Json): boolean => Number.isInteger(reply.status) && reply.status >= 200 && reply.status < 300;
function protectedMutation(table: string, reply: Json, cleanup = false): boolean {
  return reply.status === 400 && reply.data === null && object(reply.error).code === "P0001"
    && (table === "health_metrics" && object(reply.error).message === "protected_body_health_record"
      || (table === "workout_sessions" || cleanup && table === "sleep_logs") && object(reply.error).message === "protected_native_history_record");
}
function timestamp(value: unknown): number {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}T.*(?:Z|[+-]\d{2}:\d{2})$/.test(value)) return NaN;
  const day = new Date(value.slice(0, 10) + "T00:00:00Z");
  if (!Number.isFinite(day.getTime()) || day.toISOString().slice(0, 10) !== value.slice(0, 10)) return NaN;
  return Date.parse(value);
}
function dateStart(date: string, tz: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || date < "1970-01-01" || new Date(date + "T00:00:00Z").toISOString().slice(0, 10) !== date) throw new Error("invalid_history_from");
  let instant = new Date(date + "T12:00:00Z");
  const represented = dayStringInZone(instant, tz);
  instant = new Date(instant.getTime() + (Date.parse(date) - Date.parse(represented)));
  const start = startOfDayInZoneUtc(instant, tz);
  if (dayStringInZone(start, tz) !== date) throw new Error("unrepresentable_history_day");
  return start.toISOString();
}
function coverage(): Record<SyncType, HealthHistoryCoverage> {
  return Object.fromEntries(TYPES.map(type => [type, { attempted: false, read: 0, written: 0, protected: 0, unchanged: 0, complete: false }])) as Record<SyncType, HealthHistoryCoverage>;
}
function isState(value: unknown): value is HealthHistoryState {
  const s = object(value);
  return s.version === 1 && typeof s.generation === "string" && [s.from, s.to, s.next_before].every(x => Number.isFinite(timestamp(x))) &&
    timestamp(s.from) <= timestamp(s.next_before) && timestamp(s.next_before) <= timestamp(s.to) && Number.isInteger(s.completed_windows) && s.completed_windows >= 0;
}
function windowStart(from: string, to: string, tz: string, days = 28): string {
  // Civil midnight keeps adjacent aggregate windows from overwriting half a day.
  // Leave one day for sleep's preceding evening without crossing Android's
  // normal 30-day access boundary in the first window.
  let start = startOfDayInZoneUtc(new Date(timestamp(to) - (days === 1 ? 0 : 28) * DAY), tz).getTime();
  if (start === timestamp(to)) start = startOfDayInZoneUtc(new Date(start - 12 * 3_600_000), tz).getTime();
  while (timestamp(to) - start > 30 * DAY) start = startOfDayInZoneUtc(new Date(start + 36 * 3_600_000), tz).getTime();
  return new Date(Math.max(timestamp(from), start)).toISOString();
}

/** No raw paging exists in this plugin. A saturated read must split, or fail visibly. */
export async function readHealthSamplesBounded(
  Health: Pick<HealthPlugin, "readSamples">, dataType: "sleep" | "heartRateVariability" | "heartRate",
  from: string, to: string, current: () => void | Promise<void> = () => {},
): Promise<{ samples: HealthSample[]; fetched: number }> {
  const unique = new Map<string, HealthSample>();
  let calls = 0, fetched = 0;
  const read = async (a: number, b: number): Promise<void> => {
    await current();
    if (++calls > 128) throw new Error("raw_query_budget_exhausted");
    const result = await Health.readSamples({ dataType, startDate: new Date(a).toISOString(), endDate: new Date(b).toISOString(), limit: 2000 });
    await current();
    if (!Array.isArray(result.samples)) throw new Error("invalid_raw_response");
    fetched += result.samples.length;
    if (result.samples.length >= 2000) {
      if (b - a <= 60_000) throw new Error("raw_sample_limit_unresolved");
      const middle = Math.floor((a + b) / 2);
      await read(a, middle); await read(middle, b); return;
    }
    for (const sample of result.samples) {
      const start = timestamp(sample.startDate), end = timestamp(sample.endDate);
      if (!Number.isFinite(start) || !Number.isFinite(end) || end < start || (dataType === "sleep" && end === start) ||
        !Number.isFinite(sample.value) || sample.value < 0 || sample.dataType !== dataType) throw new Error("invalid_raw_sample");
      // Health Connect selects interval records, then flattens all their
      // heart-rate points. Valid points outside this leaf are not corrupt;
      // retain only its half-open point interval, without losing split edges.
      if (dataType === "heartRate" && (start < a || start >= b)) continue;
      if (dataType !== "heartRate" && (start >= b || end < a)) throw new Error("invalid_raw_sample");
      // Android flattens several heart-rate points from one Health Connect
      // record: all points share platformId, but each timestamp is distinct.
      const key = sample.platformId ? sample.platformId + (dataType === "heartRate" ? "|" + sample.startDate : "") : [sample.sourceId, sample.startDate, sample.endDate, sample.sleepState, sample.dataType].join("|");
      const previous = unique.get(key);
      if (previous && canonical(previous) !== canonical(sample)) throw new Error("conflicting_raw_duplicate");
      unique.set(key, sample);
    }
  };
  await read(timestamp(from), timestamp(to));
  return { samples: [...unique.values()], fetched };
}

/** Workouts use the plugin's in-query cursor, never a cursor between date windows. */
export async function readHealthWorkoutsBounded(
  Health: Pick<HealthPlugin, "queryWorkouts">, from: string, to: string, provider: HealthProvider,
  current: () => void | Promise<void> = () => {},
): Promise<{ workouts: Workout[]; fetched: number }> {
  const unique = new Map<string, Workout>(), anchors = new Set<string>();
  let anchor: string | undefined, fetched = 0;
  for (let page = 0; page < 25; page++) {
    await current();
    const result = await Health.queryWorkouts({ startDate: from, endDate: to, limit: 200, ...(anchor ? { anchor } : {}) });
    await current();
    if (!Array.isArray(result.workouts)) throw new Error("invalid_workout_response");
    fetched += result.workouts.length;
    for (const workout of result.workouts) {
      const start = timestamp(workout.startDate), end = timestamp(workout.endDate);
      if (!Number.isFinite(start) || !Number.isFinite(end) || end < start || !Number.isFinite(workout.duration) || workout.duration < 0 || start >= timestamp(to) || end < timestamp(from)) throw new Error("invalid_workout_sample");
      const key = workout.platformId || workout.workoutType + ":" + start;
      const previous = unique.get(key);
      if (previous && canonical(previous) !== canonical(workout)) throw new Error("conflicting_workout_duplicate");
      unique.set(key, workout);
    }
    if (!result.anchor) return { workouts: [...unique.values()].filter(w => timestamp(w.startDate) >= timestamp(from)), fetched };
    if (typeof result.anchor !== "string" || result.workouts.length === 0 || anchors.has(result.anchor)) throw new Error("workout_cursor_not_progressing");
    // HealthKit advances by timestamp ±1ms: ties at a full page boundary can be lost.
    if (provider === "apple_health" && result.workouts.length >= 200) {
      const boundary = timestamp(result.workouts.at(-1)!.startDate);
      await current();
      const probe = await Health.queryWorkouts({ startDate: new Date(boundary).toISOString(), endDate: new Date(Math.min(timestamp(to), boundary + 1)).toISOString(), limit: 200 });
      await current();
      if (!Array.isArray(probe.workouts) || probe.workouts.length >= 200 || probe.anchor) throw new Error("workout_cursor_timestamp_tie");
      fetched += probe.workouts.length;
      const observed = result.workouts.filter(w => timestamp(w.startDate) === boundary);
      for (const w of observed) if (!probe.workouts.some(p => (p.platformId || p.workoutType + ":" + timestamp(p.startDate)) === (w.platformId || w.workoutType + ":" + boundary))) throw new Error("workout_cursor_boundary_not_confirmed");
      for (const w of probe.workouts) {
        if (!Number.isFinite(timestamp(w.startDate)) || !Number.isFinite(timestamp(w.endDate)) || timestamp(w.endDate) < timestamp(w.startDate) || timestamp(w.startDate) >= boundary + 1 || timestamp(w.endDate) < boundary || !Number.isFinite(w.duration) || w.duration < 0) throw new Error("invalid_workout_sample");
        if (timestamp(w.startDate) !== boundary) continue;
        const key = w.platformId || w.workoutType + ":" + boundary, previous = unique.get(key);
        if (previous && canonical(previous) !== canonical(w)) throw new Error("conflicting_workout_duplicate");
        unique.set(key, w);
      }
    }
    anchors.add(result.anchor); anchor = result.anchor;
  }
  throw new Error("workout_page_limit_exhausted");
}

/** A bounded, resumable owner-authenticated history run. No absence deletes data. */
export async function runHealthHistorySync(options: HealthHistoryOptions): Promise<HealthHistoryResult> {
  const { Health, sb, userId, provider } = options;
  const now = options.now ?? new Date();
  let tz = options.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
  let timezoneSource = options.timezone ? "explicit" : "device";
  let timezoneWarning: string | undefined;
  let permissionSnapshot: Json | undefined;
  // Reject a corrupt timezone instead of silently relabeling imported daily keys.
  new Intl.DateTimeFormat("en", { timeZone: tz }).format(now);
  const runId = crypto.randomUUID();
  let history: HealthHistoryState;
  let claimed = false, synced = 0, totalFetched = 0;
  let advanced = false;
  type Touched = {
    table: string; row: Json; type: SyncType; acknowledged: boolean; protectedCounted: boolean; unchangedCounted: boolean;
    mutation: "ready" | "preserved" | "deleted" | "uncertain"; failure?: unknown;
  };
  const touched = new Map<string, Touched>();
  const errors: string[] = [];
  const local = () => { if (options.current && !options.current()) throw new Error("health_session_changed"); };
  const connection = async (requireRun = false): Promise<Json> => {
    local();
    const auth = await sb.auth.getUser(); local();
    if (auth.error || auth.data?.user?.id !== userId) throw new Error("health_session_changed");
    const { data, error } = await sb.from("user_connections").select("id,user_id,provider,status,provider_metadata")
      .eq("user_id", userId).eq("id", options.connection.id).eq("provider", provider).eq("status", "active").maybeSingle();
    local();
    if (error) throw error;
    if (!data || data.id !== options.connection.id || data.user_id !== userId || data.provider !== provider || data.status !== "active") throw new Error("health_connection_changed");
    if (requireRun && (object(object(data.provider_metadata).history_sync).run_id !== runId || object(object(data.provider_metadata).history_sync).generation !== history.generation)) throw new Error("health_history_generation_changed");
    return data;
  };
  const check = async () => { await connection(true); };
  const mutate = (table: HealthHistoryTable, operation: "insert" | "update" | "delete" | "sleep_import", record: Json | Json[], expected: { id: string; metadata?: unknown } | null = null) => {
    if (!sb.rpc) throw new Error("health_history_mutation_unavailable");
    return mutateHealthHistory({ rpc: sb.rpc.bind(sb) }, {
      userId, connectionId: options.connection.id, provider, generation: history.generation, runId,
    }, { table, operation, record, expected }, check);
  };
  const preserve = (item: Touched) => {
    if (item.unchangedCounted) {
      const result = history.coverage_by_type[item.type];
      result.unchanged = (result.unchanged ?? 0) - 1; item.unchangedCounted = false;
    }
    if (!item.protectedCounted) { history.coverage_by_type[item.type].protected++; item.protectedCounted = true; }
  };
  // deleteImported writes an authoritative tombstone before deleting its row.
  // A deletion during a native read or write must win over a delayed importer.
  const removeLateDeleted = async (table: string, row: Json): Promise<"none" | "deleted" | "preserved_conflict"> => {
    const item = touched.get(table + "|" + row.external_id)!;
    if (item.mutation === "uncertain") throw item.failure;
    if (item.mutation === "preserved") return "preserved_conflict";
    if (item.mutation === "deleted") return "deleted";
    await check();
    const tomb = await sb.from("integration_deletions").select("id")
      .eq("user_id", userId).eq("source", provider).eq("table_name", table).eq("external_id", row.external_id).limit(1);
    await check();
    if (tomb.error) throw tomb.error;
    if (!Array.isArray(tomb.data)) throw new Error("health_tombstone_recheck_not_confirmed");
    if (!tomb.data.length) return "none";
    // Once a mutation starts, an uncertain outcome is terminal for this origin
    // in this run. Neither reconciliation pass may resend it to seek evidence.
    item.mutation = "uncertain";
    item.failure = new Error("health_tombstone_cleanup_not_confirmed");
    try {
      const removed = await mutate(table as HealthHistoryTable, "delete", { user_id: userId, source: provider, external_id: row.external_id });
      await check();
      if (protectedMutation(table, removed, true)) {
        item.mutation = "preserved"; preserve(item);
        return "preserved_conflict";
      }
      if (removed.error) throw removed.error;
      if (!successful(removed) || !Array.isArray(removed.data) || removed.data.length > 1
        || removed.data.some(row => typeof object(row).id !== "string" || !object(row).id.trim())) throw new Error("health_tombstone_cleanup_not_confirmed");
      const back = await sb.from(table).select("id").eq("user_id", userId).eq("source", provider).eq("external_id", row.external_id).limit(1);
      await check();
      if (back.error) throw back.error;
      if (!successful(back) || !Array.isArray(back.data) || back.data.length) throw new Error("health_tombstone_cleanup_not_confirmed");
      item.mutation = "deleted"; preserve(item);
      if (item.acknowledged) {
        item.acknowledged = false; synced--; history.coverage_by_type[item.type].written--;
      }
      return "deleted";
    } catch (error) {
      item.failure = error;
      history.coverage_by_type[item.type].complete = false;
      history.coverage_by_type[item.type].reason = message(error);
      throw error;
    }
  };
  const reconcileLateDeleted = async () => {
    for (const item of touched.values()) await removeLateDeleted(item.table, item.row);
  };
  const save = async (row: Json, state: HealthHistoryState, stamp = false) => {
    local();
    const metadata = { ...object(row.provider_metadata), ...permissionSnapshot, history_sync: state };
    let query = sb.from("user_connections").update({ provider_metadata: metadata,
      ...(stamp ? { last_sync_at: new Date().toISOString(), last_sync_status: state.status === "partial" || state.status === "blocked" ? "partial" : "ok", last_sync_error: errors.length ? errors.join("; ").slice(0, 500) : null } : {}),
    }).eq("id", row.id).eq("user_id", userId).eq("provider", provider).eq("status", "active");
    query = row.provider_metadata == null ? query.is("provider_metadata", null) : query.eq("provider_metadata", JSON.stringify(row.provider_metadata));
    const result = await query.select("id,provider_metadata"); local();
    const auth = await sb.auth.getUser(); local();
    if (auth.error || auth.data?.user?.id !== userId) throw new Error("health_session_changed");
    if (result.error) throw result.error;
    if (result.data?.length !== 1 || result.data[0].id !== row.id || canonical(object(result.data[0].provider_metadata).history_sync) !== canonical(state)) throw new Error("health_history_checkpoint_not_confirmed");
  };
  const initial = await connection();
  assertHealthSyncAvailable(initial.provider_metadata, now.getTime(), "health_history_run_in_progress");
  const previous = object(initial.provider_metadata).history_sync;
  if (options.mode === "history_continue") {
    if (!isState(previous)) throw new Error("health_history_not_started");
    history = { ...previous, coverage_by_type: coverage(), window: { ...previous.window } };
    if (typeof previous.timezone === "string") {
      new Intl.DateTimeFormat("en", { timeZone: previous.timezone }).format(now);
      tz = previous.timezone; timezoneSource = previous.timezone_source ?? "persisted";
    }
  } else {
    if (!options.timezone) {
      try {
        local();
        const preference = await sb.from("user_whisper_preferences").select("timezone").eq("user_id", userId).maybeSingle(); local();
        if (preference.error) throw preference.error;
        if (preference.data?.timezone) {
          new Intl.DateTimeFormat("en", { timeZone: preference.data.timezone }).format(now);
          tz = preference.data.timezone; timezoneSource = "profile";
        }
      } catch (error) {
        local(); timezoneWarning = "profile_timezone_unavailable";
        console.warn("[healthHistorySync] profile timezone unavailable; using device timezone", error);
      }
    }
    const defaultDate = new Date(now); defaultDate.setUTCFullYear(defaultDate.getUTCFullYear() - 1);
    const from = dateStart(options.history_from ?? dayStringInZone(defaultDate, tz), tz), to = now.toISOString();
    if (timestamp(from) >= timestamp(to)) throw new Error("invalid_history_from");
    history = { version: 1, generation: crypto.randomUUID(), from, to, next_before: to, completed_windows: 0, status: "in_progress", window: { from, to, complete: false }, coverage_by_type: coverage() };
  }
  if (history.next_before === history.from && history.status === "complete") return { synced, total_fetched: totalFetched, errors, status: "ok", history };
  history.window = { from: windowStart(history.from, history.next_before, tz, history.window_days === 1 ? 1 : 28), to: history.next_before, complete: false };
  history.timezone = tz;
  history.timezone_source = timezoneSource;
  if (timezoneWarning) history.timezone_warning = timezoneWarning;
  history.visibility = provider === "apple_health" ? "unknown" : "authorized";
  history.coverage_scope = provider === "apple_health" ? "visible_data" : "authorized_data";
  history.permission_evidence = provider === "apple_health" ? "ios_read_grant_unobservable" : "current_device_authorization";
  history.run_id = runId; history.lease_until = new Date(now.getTime() + 120_000).toISOString();
  history.status = "in_progress"; delete history.reason;
  await save(initial, history); claimed = true;
  try {
    const verdict = await Health.checkAuthorization({ read: [...HEALTH_READ_TYPES], requestHistoryAccess: true }); await check();
    permissionSnapshot = {
      read_authorized: HEALTH_READ_TYPES.filter(type => verdict.readAuthorized?.includes(type)),
      read_denied: HEALTH_READ_TYPES.filter(type => verdict.readDenied?.includes(type)),
      permission_checked_at: new Date().toISOString(),
      history_access: verdict.historyAccessAuthorized ?? null,
      history_access_available: verdict.historyAccessAvailable ?? null,
    };
    const oldWindow = timestamp(history.window.from) < now.getTime() - 30 * DAY;
    if (provider === "health_connect" && oldWindow && (verdict.historyAccessAvailable !== true || verdict.historyAccessAuthorized !== true)) {
      history.status = "blocked"; history.reason = verdict.historyAccessAvailable === false ? "history_access_unavailable" : "history_access_required";
      errors.push(history.reason);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- PostgREST builder bridge for new schema columns.
      const paged = async (base: () => any): Promise<Json[]> => {
        const rows: Json[] = [], ids = new Set<string>();
        for (let page = 0; page < 40; page++) {
          await check();
          const result = await base().order("id", { ascending: true }).range(page * PAGE, (page + 1) * PAGE - 1); await check();
          if (result.error) throw result.error;
          if (!successful(result) || !Array.isArray(result.data) || result.data.length > PAGE) throw new Error("health_existing_read_not_confirmed");
          for (const row of result.data) {
            if (!row || typeof row !== "object" || Array.isArray(row) || typeof row.id !== "string" || !row.id.trim() || ids.has(row.id)) throw new Error("health_existing_read_not_confirmed");
            ids.add(row.id); rows.push(row);
          }
          if (result.data.length < PAGE) return rows;
        }
        throw new Error("health_database_page_limit_exhausted");
      };
      const tombstones = await paged(() => sb.from("integration_deletions").select("id,table_name,source,external_id").eq("user_id", userId).eq("source", provider));
      const dead = new Set(tombstones.map(t => t.table_name + "|" + t.external_id));
      for (const type of TYPES) {
        const result = history.coverage_by_type[type];
        if (provider === "health_connect" && !verdict.readAuthorized?.includes(READ_TYPE[type])) { result.reason = "permission_denied"; continue; }
        result.attempted = true;
        try {
          let rows: Json[] = [], table: string;
          if (type === "sleep") {
            const sleepStart = new Date(timestamp(history.window.from) - DAY).toISOString();
            const sleepEnd = new Date(Math.min(timestamp(history.to), timestamp(history.window.to) + DAY)).toISOString();
            const read = await readHealthSamplesBounded(Health, "sleep", sleepStart, sleepEnd, check);
            result.read = read.samples.length; totalFetched += read.fetched;
            const lowerDay = dayStringInZone(new Date(history.window.from), tz), upperDay = dayStringInZone(new Date(history.window.to), tz);
            const atMidnight = startOfDayInZoneUtc(new Date(history.window.to), tz).toISOString() === history.window.to;
            rows = buildSleepRows(read.samples, userId, provider, tz).filter(r => r.sleep_date >= lowerDay && (atMidnight ? r.sleep_date < upperDay : r.sleep_date <= upperDay)); table = "sleep_logs";
          } else if (type === "workouts") {
            const read = await readHealthWorkoutsBounded(Health, history.window.from, history.window.to, provider, check);
            result.read = read.workouts.length; totalFetched += read.fetched;
            rows = buildWorkoutRows(read.workouts, userId, provider); table = "workout_sessions";
          } else {
            let buckets;
            if (type === "hrv" || type === "heart_rate") {
              const read = await readHealthSamplesBounded(Health, type === "hrv" ? "heartRateVariability" : "heartRate", history.window.from, history.window.to, check);
              result.read = read.samples.length; totalFetched += read.fetched;
              buckets = aggregateSamplesByDay(read.samples.filter(s => timestamp(s.startDate) >= timestamp(history.window.from)), "average", tz)
                .map(bucket => ({ ...bucket, startDate: new Date(Math.max(timestamp(bucket.startDate), timestamp(history.window.from))).toISOString(), endDate: new Date(Math.min(timestamp(bucket.endDate), timestamp(history.window.to))).toISOString() }));
            } else {
              // Native step aggregation retains HealthKit/Health Connect source
              // priority. Query each exact civil day, then combine its native
              // segments (device midnight / Android 24h boundaries can differ).
              buckets = [];
              for (let start = timestamp(history.window.from); start < timestamp(history.window.to);) {
                const next = startOfDayInZoneUtc(new Date(start + 36 * 3_600_000), tz).getTime();
                const end = Math.min(next, timestamp(history.window.to));
                if (end <= start) throw new Error("invalid_civil_day_boundary");
                await check();
                const read = await Health.queryAggregated({ dataType: "steps", startDate: new Date(start).toISOString(), endDate: new Date(end).toISOString(), bucket: "day", aggregation: "sum" }); await check();
                if (!Array.isArray(read.samples)) throw new Error("invalid_aggregate_response");
                const sorted = [...read.samples].sort((a, b) => timestamp(a.startDate) - timestamp(b.startDate));
                if (sorted.some((s, i) => !Number.isFinite(timestamp(s.startDate)) || !Number.isFinite(timestamp(s.endDate)) || timestamp(s.endDate) <= timestamp(s.startDate) || timestamp(s.endDate) <= start || timestamp(s.startDate) >= end || !Number.isFinite(s.value) || s.value < 0 || i > 0 && timestamp(sorted[i - 1].endDate) > timestamp(s.startDate))) throw new Error("invalid_step_aggregate_segments");
                result.read += sorted.length; totalFetched += sorted.length;
                if (sorted.length) buckets.push({ startDate: new Date(start).toISOString(), endDate: new Date(end).toISOString(), value: sorted.reduce((sum, s) => sum + s.value, 0) });
                start = end;
              }
            }
            if (buckets.some(s => !Number.isFinite(timestamp(s.startDate)) || !Number.isFinite(timestamp(s.endDate)) || timestamp(s.endDate) <= timestamp(s.startDate) || !Number.isFinite(s.value) || s.value < 0)) throw new Error("invalid_aggregate_sample");
            rows = buildMetricRows(buckets, userId, provider, type, type === "steps" ? "count" : type === "hrv" ? "millisecond" : "bpm", tz); table = "health_metrics";
          }
          const rowKeys = new Map<string, Json>();
          for (const row of rows) {
            const key = table === "sleep_logs" ? row.sleep_date : row.external_id;
            if (rowKeys.has(key) && canonical(rowKeys.get(key)) !== canonical(row)) throw new Error("conflicting_mapped_duplicate");
            rowKeys.set(key, row);
          }
          rows = [...rowKeys.values()];
          // Existing rows are read by bounded natural-key chunks; metadata survives refresh.
          for (let offset = 0; offset < rows.length; offset += 100) {
            const batch = rows.slice(offset, offset + 100), keyColumn = table === "sleep_logs" ? "sleep_date" : "external_id";
            if (table === "sleep_logs") {
              await check();
              const imported = await importSleepRecords({ rpc: (name, args) => {
                if (name !== "import_sleep_records" || !Array.isArray(args.p_records)) throw new Error("invalid_health_history_mutation");
                return mutate("sleep_logs", "sleep_import", args.p_records as Json[]);
              } }, batch as SleepImportRow[], userId, local);
              await check();
              result.written += imported.written; result.protected += imported.protected; synced += imported.written;
              for (let index = 0; index < batch.length; index++) touched.set(table + "|" + batch[index].external_id, {
                table, row: batch[index], type, acknowledged: imported.results[index].status !== "skipped",
                protectedCounted: imported.results[index].status === "skipped", unchangedCounted: false, mutation: "ready",
              });
              continue;
            }
            const existing = await paged(() => {
              let query = sb.from(table).select("*").eq("user_id", userId).in(keyColumn, batch.map(r => r[keyColumn]));
              if (table !== "sleep_logs") query = query.eq("source", provider);
              return query;
            });
            if (existing.some(row => row.user_id !== userId || typeof row.id !== "string" || !batch.some(candidate => candidate[keyColumn] === row[keyColumn]) || table !== "sleep_logs" && row.source !== provider)) throw new Error("health_existing_row_mismatch");
            if (new Set(existing.map(row => row[keyColumn])).size !== existing.length) throw new Error("health_existing_read_not_confirmed");
            const byKey = new Map(existing.map(r => [r[keyColumn], r]));
            for (const row of batch) {
              await check();
              const touchedKey = table + "|" + row.external_id;
              const item: Touched = { table, row, type, acknowledged: false, protectedCounted: false, unchangedCounted: false, mutation: "ready" };
              touched.set(touchedKey, item);
              const old = byKey.get(row[keyColumn]);
              const payload = table === "health_metrics" ? { ...row, metadata: row.metadata == null
                ? old?.metadata ?? null : { ...object(old?.metadata), ...object(row.metadata) } } : row;
              // PostgreSQL JSON numbers can exceed JavaScript's finite range.
              // JSON.stringify would turn them into null; never compare, resend
              // or clean up an origin whose current JSON cannot be represented.
              if (old && !jsonValue(old) || !jsonValue(payload)) {
                item.mutation = "uncertain";
                item.failure = new Error("health_existing_readback_not_confirmed");
                throw item.failure;
              }
              if (dead.has(table + "|" + row.external_id) || old && (old.edited_at || object(old.metadata).edited_at || old.source !== provider)) { preserve(item); continue; }
              if (await removeLateDeleted(table, row) !== "none") continue;
              if (old && matchesPayload(old, payload, true)) {
                // A prior run can commit an INSERT and lose its acknowledgement.
                // An identical visible row needs no PATCH, but the paged snapshot
                // alone cannot count it: confirm its identity and content afresh.
                item.mutation = "uncertain";
                item.failure = new Error("health_existing_readback_not_confirmed");
                try {
                  const back = await sb.from(table).select("*").eq("user_id", userId).eq("source", provider)
                    .eq(keyColumn, row[keyColumn]).eq("id", old.id).maybeSingle(); await check();
                  const saved = back.data;
                  if (back.error || !successful(back) || !saved || typeof saved !== "object" || Array.isArray(saved) || !jsonValue(saved)
                    || saved.id !== old.id || saved.user_id !== userId || saved.source !== provider || saved[keyColumn] !== row[keyColumn]) throw item.failure;
                  if (saved.edited_at || object(saved.metadata).edited_at) {
                    item.mutation = "ready"; preserve(item); continue;
                  }
                  if (!matchesPayload(saved, payload, true)) throw item.failure;
                  item.mutation = "ready"; item.unchangedCounted = true;
                  result.unchanged = (result.unchanged ?? 0) + 1;
                } catch (error) { item.failure = error; throw error; }
                await removeLateDeleted(table, row);
                continue;
              }
              item.mutation = "uncertain";
              item.failure = new Error("health_write_not_confirmed");
              try {
                const written = await mutate(table as HealthHistoryTable, old ? "update" : "insert", payload,
                  old ? { id: old.id, ...(table === "health_metrics" ? { metadata: old.metadata ?? null } : {}) } : null); await check();
                if (protectedMutation(table, written)) { item.mutation = "preserved"; preserve(item); continue; }
                if (written.error) throw written.error;
                if (!successful(written) || !Array.isArray(written.data) || written.data.length !== 1 || typeof written.data[0]?.id !== "string"
                  || !jsonValue(written.data[0])
                  || table === "health_metrics" && !matchesPayload(written.data[0], payload, true)) throw new Error("health_write_not_confirmed");
                // A full ACK still needs an independent current readback before
                // counting the write or allowing a later cleanup mutation.
                let readbackQuery = sb.from(table).select("*").eq("user_id", userId).eq(keyColumn, row[keyColumn]);
                if (table !== "sleep_logs") readbackQuery = readbackQuery.eq("source", provider);
                const back = await readbackQuery.maybeSingle(); await check();
                if (back.error) throw back.error;
                const saved = back.data;
                if (!successful(back) || !saved || !jsonValue(saved) || saved.user_id !== userId || saved[keyColumn] !== row[keyColumn] || saved.source !== provider || written.data[0].id !== saved.id) throw new Error("health_write_readback_not_confirmed");
                if (saved.edited_at || object(saved.metadata).edited_at || saved.source !== provider) { item.mutation = "ready"; preserve(item); continue; }
                if (!matchesPayload(saved, payload, table === "health_metrics")) throw new Error("health_write_readback_not_confirmed");
                item.mutation = "ready"; item.acknowledged = true;
                result.written++; synced++;
              } catch (error) { item.failure = error; throw error; }
              await removeLateDeleted(table, row);
            }
          }
          result.complete = true;
          if (provider === "apple_health" && result.read === 0) result.reason = "empty_or_read_denied";
        } catch (error) {
          result.reason = message(error); errors.push(type + ": " + result.reason);
          if (result.reason === "raw_query_budget_exhausted") history.window_days = 1;
          if (/session_changed|connection_changed|generation_changed|protected_body_health_identity/.test(result.reason)) throw error;
        }
      }
      await reconcileLateDeleted();
      const attempted = TYPES.filter(type => history.coverage_by_type[type].attempted);
      // iOS privacy prevents proving read grant. Process API-visible intervals
      // even when empty, preserving the unknown visibility warning throughout.
      if (!attempted.length) {
        history.status = "blocked"; history.reason = "no_authorized_types"; errors.push(history.reason);
      } else if (attempted.every(type => history.coverage_by_type[type].complete)) {
        history.window.complete = true; history.next_before = history.window.from; history.completed_windows++;
        advanced = true;
        history.status = history.next_before === history.from ? "complete" : "in_progress";
      } else { history.status = "partial"; history.reason = "window_incomplete"; }
    }
  } catch (error) {
    errors.push(message(error)); history.status = "partial"; history.reason = message(error);
  }
  if (claimed) {
    history.run_id = null; history.lease_until = null;
    const holdCheckpoint = (error: unknown) => {
      errors.push(message(error)); history.status = "partial"; history.reason = message(error);
      // An unconfirmed checkpoint cannot advertise progress locally.
      history.next_before = history.window.to; history.window.complete = false;
      if (advanced) { history.completed_windows = Math.max(0, history.completed_windows - 1); advanced = false; }
    };
    try { await reconcileLateDeleted(); } catch (error) { holdCheckpoint(error); }
    // Record an honest partial checkpoint and release the lease even after a
    // terminal cleanup failure. Ownership/generation and CAS still gate it.
    try { const fresh = await connection(true); await save(fresh, history, true); }
    catch (error) { holdCheckpoint(error); }
  }
  return { synced, total_fetched: totalFetched, errors, status: history.status === "partial" || history.status === "blocked" ? "partial" : "ok", history };
}
